import { ViewerCamerasController } from "./utils/ViewerCamerasController";
import { ViewerControlsController } from "./utils/ViewerControlsController";
import { ViewerEventsController } from "./utils/ViewerEventsController";
import { ViewerWebGLController } from "./utils/ViewerWebglController";
import { ViewerAudioController } from "./utils/ViewerAudioController";
import { Raycaster } from "three";
import { Poly } from "../Poly";
const HOVERED_CLASS_NAME = "hovered";
const _TypedViewer = class {
  constructor(options) {
    this._active = false;
    this._doRender = true;
    this.raycaster = this.createRaycaster();
    this._mounted = false;
    this._controlsController = new ViewerControlsController(this);
    this._onBeforeTickCallbacks = [];
    this._onAfterTickCallbacks = [];
    this._onBeforeRenderCallbacks = [];
    this._onAfterRenderCallbacks = [];
    this._id = _TypedViewer._nextId();
    this._camera = options.camera;
    this._scene = options.scene;
    this._canvas = options.canvas;
    if (options.autoRender != null) {
      this._doRender = options.autoRender;
    }
    this.updateCameraAspect = options.updateCameraAspect;
    this.scene().viewersRegister.registerViewer(this);
  }
  static _nextId() {
    return `${_TypedViewer._nextViewerId++}`;
  }
  mount(element) {
    this._domElement = element;
    this._domElement.viewer = this;
    this._domElement.scene = this._scene;
    this._domElement.Poly = Poly;
    this.controlsController().mount();
    this._mounted = true;
  }
  unmount() {
    var _a;
    if (!this._domElement) {
      return;
    }
    (_a = this._audioController) == null ? void 0 : _a.unmount();
    this._domElement.removeChild(this.canvas());
    this.controlsController().unmount();
    this._mounted = false;
  }
  static _canvasIdPrefix() {
    return "TypedViewer";
  }
  static createCanvas(id) {
    id = id || _TypedViewer._nextId();
    const canvas = document.createElement("canvas");
    canvas.id = `${this._canvasIdPrefix()}_${id}`;
    canvas.style.display = "block";
    canvas.style.outline = "none";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    return canvas;
  }
  controlsNode() {
    return this._controlsNode;
  }
  canvas() {
    return this._canvas = this._canvas || _TypedViewer.createCanvas(this._id);
  }
  setRenderObjectOverride(object) {
    if (object) {
      this._renderObjectOverride = object;
    } else {
      this._renderObjectOverride = void 0;
    }
  }
  active() {
    return this._active;
  }
  activate() {
    this._active = true;
  }
  deactivate() {
    this._active = false;
  }
  camerasController() {
    return this._camerasController = this._camerasController || new ViewerCamerasController(this);
  }
  controlsController() {
    return this._controlsController;
  }
  eventsController() {
    return this._eventsController = this._eventsController || new ViewerEventsController(this);
  }
  webglController() {
    return this._webGLController = this._webGLController || new ViewerWebGLController(this);
  }
  audioController() {
    return this._audioController = this._audioController || new ViewerAudioController(this);
  }
  domElement() {
    return this._domElement;
  }
  scene() {
    return this._scene;
  }
  createRaycaster() {
    const raycaster = new Raycaster();
    raycaster.firstHitOnly = true;
    return raycaster;
  }
  camera() {
    return this._camera;
  }
  id() {
    return this._id;
  }
  dispose() {
    this._scene.viewersRegister.unregisterViewer(this);
    this.eventsController().dispose();
    this.controlsController().unmount();
    if (!this._domElement) {
      return;
    }
    let child;
    while (child = this._domElement.children[0]) {
      this._domElement.removeChild(child);
    }
  }
  resetContainerClass() {
    var _a;
    (_a = this.domElement()) == null ? void 0 : _a.classList.remove(HOVERED_CLASS_NAME);
  }
  setContainerClassHovered() {
    var _a;
    (_a = this.domElement()) == null ? void 0 : _a.classList.add(HOVERED_CLASS_NAME);
  }
  markAsReady() {
  }
  setAutoRender(state = true) {
    this._doRender = state;
  }
  autoRenderState() {
    return this._doRender;
  }
  registerOnBeforeTick(callbackName, callback, options = {}) {
    this._registerCallback(callbackName, callback, this.registeredBeforeTickCallbacks(), options);
  }
  unRegisterOnBeforeTick(callbackName) {
    this._unregisterCallback(callbackName, this._onBeforeTickCallbacksMap);
  }
  registeredBeforeTickCallbacks() {
    return this._onBeforeTickCallbacksMap = this._onBeforeTickCallbacksMap || /* @__PURE__ */ new Map();
  }
  registerOnAfterTick(callbackName, callback, options = {}) {
    this._registerCallback(callbackName, callback, this.registeredAfterTickCallbacks(), options);
  }
  unRegisterOnAfterTick(callbackName) {
    this._unregisterCallback(callbackName, this._onAfterTickCallbacksMap);
  }
  registeredAfterTickCallbacks() {
    return this._onAfterTickCallbacksMap = this._onAfterTickCallbacksMap || /* @__PURE__ */ new Map();
  }
  registerOnBeforeRender(callbackName, callback, options = {}) {
    this._registerCallback(callbackName, callback, this.registeredBeforeRenderCallbacks(), options);
  }
  unRegisterOnBeforeRender(callbackName) {
    this._unregisterCallback(callbackName, this._onBeforeRenderCallbacksMap);
  }
  registeredBeforeRenderCallbacks() {
    return this._onBeforeRenderCallbacksMap = this._onBeforeRenderCallbacksMap || /* @__PURE__ */ new Map();
  }
  registerOnAfterRender(callbackName, callback, options = {}) {
    this._registerCallback(callbackName, callback, this.registeredAfterRenderCallbacks(), options);
  }
  unRegisterOnAfterRender(callbackName) {
    this._unregisterCallback(callbackName, this._onAfterRenderCallbacksMap);
  }
  registeredAfterRenderCallbacks() {
    return this._onAfterRenderCallbacksMap = this._onAfterRenderCallbacksMap || /* @__PURE__ */ new Map();
  }
  _registerCallback(callbackName, callback, map, options = {}) {
    if (map.has(callbackName)) {
      console.warn(`callback ${callbackName} already registered`);
      return;
    }
    map.set(callbackName, { callback, options });
    this._updateCallbacks();
  }
  _unregisterCallback(callbackName, map) {
    if (!map) {
      return;
    }
    const callbackContainer = map.get(callbackName);
    if (!callbackContainer) {
      return;
    }
    const options = callbackContainer.options;
    if (options.persistent == true) {
      return;
    }
    map.delete(callbackName);
    this._updateCallbacks();
  }
  _updateCallbacks() {
    var _a, _b, _c, _d;
    this._onBeforeTickCallbacks = [];
    (_a = this._onBeforeTickCallbacksMap) == null ? void 0 : _a.forEach((callbackContainer) => {
      this._onBeforeTickCallbacks.push(callbackContainer.callback);
    });
    this._onAfterTickCallbacks = [];
    (_b = this._onAfterTickCallbacksMap) == null ? void 0 : _b.forEach((callbackContainer) => {
      this._onAfterTickCallbacks.push(callbackContainer.callback);
    });
    this._onBeforeRenderCallbacks = [];
    (_c = this._onBeforeRenderCallbacksMap) == null ? void 0 : _c.forEach((callbackContainer) => {
      this._onBeforeRenderCallbacks.push(callbackContainer.callback);
    });
    this._onAfterRenderCallbacks = [];
    (_d = this._onAfterRenderCallbacksMap) == null ? void 0 : _d.forEach((callbackContainer) => {
      this._onAfterRenderCallbacks.push(callbackContainer.callback);
    });
  }
  _runTickCallbacks(callbacks, delta) {
    for (const callback of callbacks) {
      callback(delta);
    }
  }
  _runRenderCallbacks(callbacks, delta, renderer) {
    for (const callback of callbacks) {
      callback(delta, renderer);
    }
  }
  _runOnBeforeTickCallbacks(delta) {
    this._runTickCallbacks(this._onBeforeTickCallbacks, delta);
  }
  _runOnAfterTickCallbacks(delta) {
    this._runTickCallbacks(this._onAfterTickCallbacks, delta);
  }
  _runOnBeforeRenderCallbacks(delta, renderer) {
    this._runRenderCallbacks(this._onBeforeRenderCallbacks, delta, renderer);
  }
  _runOnAfterRenderCallbacks(delta, renderer) {
    this._runRenderCallbacks(this._onAfterRenderCallbacks, delta, renderer);
  }
};
export let TypedViewer = _TypedViewer;
TypedViewer._nextViewerId = 0;
